// src/modules/Rules/hooks/useShippingRuleForm.tsx
// Hook que centraliza la lógica de obtención de metadatos, operadores, valores y acciones relacionadas con el formulario de reglas de envío.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ConditionApiAdapter, Option, Operator, ValueResult } from '../adapters/ConditionApiAdapter';
import { RulesApiAdapter } from '../adapters/RulesApiAdapter';
import { CourierIntegrationApiAdapter, IntegrationOption } from '../adapters/CourierIntegrationApiAdapter';
import type { RulesModel } from '../models/Rules';
import globalCache from '../../../../shared/lib/GlobalCache';
import {
  COURIER_INTEGRATIONS_LIST_CACHE_KEY,
  getCachedCourierIntegrations,
  prefetchRulesReferenceData,
} from '../services/prefetchRulesReferenceData';

export interface UseShippingRuleFormOptions {
  /** Cliente (tenant_external) al crear reglas desde /clients/:clientId/rules — filtra productos/tiendas */
  teamId?: number;
}

export function useShippingRuleForm(options: UseShippingRuleFormOptions = {}) {
  const { teamId } = options;
  // inicializa una vez el HttpClient
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);

  // memoiza los adapters
  const condAdapter = useMemo(() => new ConditionApiAdapter(httpClient), [httpClient]);
  const rulesAdapter = useMemo(() => new RulesApiAdapter(httpClient), [httpClient]);
  const courierIntAdapter = useMemo(() => new CourierIntegrationApiAdapter(httpClient), [httpClient]);

  // estados
  const [conditionOptions, setConditionOptions] = useState<Option[]>([]);
  // Todas las integraciones de courier
  const [allCourierIntegrations, setAllCourierIntegrations] = useState<IntegrationOption[]>([]);
  // Integraciones de courier para mostrar en el selector (todas)
  const [courierIntegrations, setCourierIntegrations] = useState<IntegrationOption[]>([]);

  // Opciones de condición (ligero) y precarga de referencias: países, couriers, integraciones, servicios.
  useEffect(() => {
    condAdapter.getConditionOptions().then(setConditionOptions);
  }, [condAdapter]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        await prefetchRulesReferenceData();
      } catch {
        // Las piezas que hayan quedado en caché siguen siendo útiles; el formulario hace fallback.
      }
      if (cancel) return;
      let integrations = getCachedCourierIntegrations();
      if (!integrations?.length) {
        integrations = await courierIntAdapter.getAll();
        if (integrations?.length) {
          globalCache.set(COURIER_INTEGRATIONS_LIST_CACHE_KEY, integrations);
        }
      }
      if (!cancel && integrations?.length) {
        setAllCourierIntegrations(integrations);
        setCourierIntegrations(integrations);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [courierIntAdapter]);

  // Métodos con caché global
  const loadOperators = useCallback(
    async (field: string): Promise<Operator[]> => {
      return globalCache.getOrSet(`operators:${field}`, () => condAdapter.getConditionOperators(field));
    },
    [condAdapter],
  );

  const loadValues = useCallback(
    async (field: string): Promise<ValueResult> => {
      // Con cliente hijo (>0) producto/tienda usan DynamicCombobox en RuleConditionsSection, no esta lista global
      if (Number(teamId) > 0 && (field === 'product_id' || field === 'shop_id')) {
        return { valueType: 'select', options: [] };
      }
      return globalCache.getOrSet(`values:${field}`, () => condAdapter.getConditionValues(field));
    },
    [condAdapter, teamId],
  );

  const loadCourierProducts = useCallback(
    async (integrationId: string): Promise<any[]> => {
      return globalCache.getOrSet(`courierProducts:${integrationId}`, () =>
        courierIntAdapter.getProducts(integrationId),
      );
    },
    [courierIntAdapter],
  );

  // Buscar el courier integration que tiene un servicio específico
  const findCourierByServiceId = useCallback(
    async (serviceId: string): Promise<string | null> => {
      if (!serviceId || allCourierIntegrations.length === 0) return null;

      // Buscar en caché primero (usamos allCourierIntegrations para incluir inactivas)
      for (const integration of allCourierIntegrations) {
        const cacheKey = `courierProducts:${integration.id}`;
        const cached = globalCache.get(cacheKey);
        if (cached) {
          const services = cached as any[];
          if (services.find((s) => s.id === serviceId)) {
            return String(integration.id);
          }
        }
      }

      // Si no está en caché, buscar cargando servicios de cada courier
      for (const integration of allCourierIntegrations) {
        try {
          const services = await loadCourierProducts(String(integration.id));
          if (services.find((s) => s.id === serviceId)) {
            return String(integration.id);
          }
        } catch {
          continue;
        }
      }

      return null;
    },
    [allCourierIntegrations, loadCourierProducts],
  );

  // Obtener información de una integración por ID (incluidas inactivas)
  const getCourierIntegrationById = useCallback(
    (integrationId: string): IntegrationOption | undefined => {
      return allCourierIntegrations.find((i) => i.id === integrationId);
    },
    [allCourierIntegrations],
  );

  /** Sin caché: al entrar en edición debe reflejar siempre el estado actual del servidor. */
  const getRuleById = useCallback(
    async (ruleId: number): Promise<RulesModel> => {
      return rulesAdapter.getById(ruleId);
    },
    [rulesAdapter],
  );

  const createRule = useCallback(
    (payload: any): Promise<void> => httpClient.post('/shipping-rules', payload),
    [httpClient],
  );

  const updateRule = useCallback(
    (ruleId: number, payload: any): Promise<void> => rulesAdapter.updateRule(ruleId, payload),
    [rulesAdapter],
  );

  return {
    conditionOptions,
    courierIntegrations,
    allCourierIntegrations,
    loadOperators,
    loadValues,
    loadCourierProducts,
    findCourierByServiceId,
    getCourierIntegrationById,
    getRuleById,
    createRule,
    updateRule,
  };
}
